import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthModal = ({ onClose }) => {
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? 'Connexion réussie!' : 'Compte créé avec succès!');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-obsidian/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-porcelain p-8 md:p-12 max-w-md w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            data-testid="auth-modal-close-btn"
            className="absolute top-6 right-6 text-charcoal hover:text-primary transition-colors"
          >
            <X className="w-6 h-6" />
          </button>

          <h2 className="font-serif text-4xl text-charcoal mb-2">
            {isLogin ? 'Connexion' : 'Inscription'}
          </h2>
          <p className="text-taupe mb-8">Accédez à tout le contenu premium</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {!isLogin && (
              <div>
                <input
                  type="text"
                  placeholder="Nom complet"
                  data-testid="auth-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-4 text-charcoal placeholder:text-taupe"
                  required={!isLogin}
                />
              </div>
            )}

            <div>
              <input
                type="email"
                placeholder="Email"
                data-testid="auth-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-4 text-charcoal placeholder:text-taupe"
                required
              />
            </div>

            <div>
              <input
                type="password"
                placeholder="Mot de passe"
                data-testid="auth-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full border-b border-primary/30 bg-transparent focus:border-primary focus:outline-none px-0 py-4 text-charcoal placeholder:text-taupe"
                required
              />
            </div>

            <button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full bg-primary text-white hover:bg-primary/90 px-8 py-4 uppercase tracking-widest font-serif transition-all duration-500 ease-out disabled:opacity-50"
            >
              {loading ? 'Chargement...' : isLogin ? 'Se connecter' : "S'inscrire"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              data-testid="auth-toggle-btn"
              className="text-primary hover:text-primary/80 text-sm uppercase tracking-wide"
            >
              {isLogin ? "Créer un compte" : "Déjà membre? Se connecter"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AuthModal;